/**
 * HIPApp - Hurricane Impact Predictions Application
 *
 * Main page component for HIP - allows users to visualize hurricane forecasts,
 * adjust scenario parameters, and view impact predictions.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { trackRumAction } from "@/lib/datadog";
import { Navigation, ChevronDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/layout/Header";
import { MapLoaderOverlay } from "@/components/ui/loader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  BaseMap,
  GeoJSONLayer,
  FitBoundsToTrack,
} from "@/components/maps/LeafletMap";
import {
  ScenarioControls,
  HIPImpactsTable,
  HIPInfoCard,
  MapLegend,
  MapPropertySelector,
} from "@/components/maps/insets";
import { Polyline, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import type { PathOptions } from "leaflet";
import {
  useStorms,
  useStormMetadata,
  useStormScenario,
  useTrackGeoJSON,
  useStormImpacts,
  useStormImpactModels,
  useDisplayableApplications,
} from "@/hooks/api";
import type {
  NamedStorm,
  ImpactForecast,
  ScenarioDeviations,
  StormImpactModelDetailed,
  StormImpactVariable,
  StormImpactVariableBin,
  ScenarioQueryParams,
} from "@/api/types";
import type { ApiError } from "@/api";

// Current position marker - blue pin
const hurricaneIcon = L.divIcon({
  className: "hurricane-marker",
  html: `<div style="
    width: 24px;
    height: 32px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));
  ">
    <svg width="24" height="32" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" fill="#0284c7" stroke="#fff" stroke-width="2"/>
      <circle cx="12" cy="11" r="5" fill="#fff"/>
    </svg>
  </div>`,
  iconSize: [24, 32],
  iconAnchor: [12, 32],
});

// Storm origin marker
const stormOriginIcon = L.icon({
  iconUrl: "/leaflet/hurricane-icon.png",
  iconSize: [36, 36],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

function getCategoryFromWind(windSpeed: number): number {
  if (windSpeed >= 157) return 5;
  if (windSpeed >= 130) return 4;
  if (windSpeed >= 111) return 3;
  if (windSpeed >= 96) return 2;
  if (windSpeed >= 74) return 1;
  return 0; // Tropical Storm or lower
}

export interface HIPAppProps {
  appSlug?: string;
}

export default function HIPApp({ appSlug = "hip" }: HIPAppProps) {
  // ============================================================================
  // State
  // ============================================================================
  const [selectedStorm, setSelectedStorm] = useState<NamedStorm | null>(null);
  const [isCompactLayout, setIsCompactLayout] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 1023px)").matches
      : false,
  );
  const [selectedModel, setSelectedModel] = useState<string>("OFCL");
  const [deviations, setDeviations] = useState<ScenarioDeviations>({
    intensity: 100,
    track: 0,
    speed: 100,
  });
  const [selectedProperty, setSelectedProperty] =
    useState<string>("customersAffected");
  const [selectedImpactModel, setSelectedImpactModel] =
    useState<StormImpactModelDetailed | null>(null);
  const [legendCollapsed, setLegendCollapsedInternal] = useState(false);
  // Tracks whether the user has explicitly toggled the legend. Once set, the
  // breakpoint listener stops overriding the user's choice.
  const userHasToggledLegendRef = useRef(false);
  const setLegendCollapsed = useCallback((value: boolean) => {
    userHasToggledLegendRef.current = true;
    setLegendCollapsedInternal(value);
  }, []);
  // Manual update mode — Run Model button triggers re-fetch.
  // This prevents map flickering while adjusting sliders.
  const autoUpdate = false;
  // Track when data is being rendered to keep loading notification visible
  const [isRendering, setIsRendering] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isStormTransition, setIsStormTransition] = useState(false);
  const [holdEmptyScenarioData, setHoldEmptyScenarioData] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const handleChange = () => setIsCompactLayout(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (!userHasToggledLegendRef.current) {
      setLegendCollapsedInternal(isCompactLayout);
    }
  }, [isCompactLayout]);

  // ============================================================================
  // Data Fetching with React Query
  // ============================================================================

  // Fetch storms list (includes both active and inactive)
  const { data: storms = [], loading: stormsLoading } = useStorms(false);

  // Resolve application ID from slug (needed for direct URL navigation)
  const { data: applications } = useDisplayableApplications();
  const applicationId = useMemo(() => {
    if (applications?.length && appSlug) {
      const app = applications.find((a) => a.slug === appSlug);
      if (app?.id) return app.id;
    }
    return typeof window !== "undefined" ? localStorage.getItem("current_application_id") : null;
  }, [applications, appSlug]);

  // Fetch impact models
  const { data: impactModels = [], loading: impactModelsLoading } =
    useStormImpactModels(applicationId ?? undefined);

  // Set default selected storm when storms load
  useEffect(() => {
    if (storms.length > 0 && !selectedStorm) {
      setSelectedStorm(storms[0]);
    }
  }, [storms, selectedStorm]);

  // Set default impact model when models load
  useEffect(() => {
    if (impactModels.length > 0 && !selectedImpactModel) {
      const hipModel =
        impactModels.find((m) => m.model_slug === "hip-research") ||
        impactModels[0];
      setSelectedImpactModel(hipModel);
    }
  }, [impactModels, selectedImpactModel]);

  // Fetch metadata for selected storm
  const {
    data: metadata,
    loading: metadataLoading,
    isFetching: metadataFetching,
  } = useStormMetadata(
    selectedStorm?.id || null,
  );

  // Set default model when metadata loads
  useEffect(() => {
    if (metadata?.weather_models && metadata.weather_models.length > 0) {
      // Prefer the first model that actually has a scenario grid (e.g. HWRF
      // for early-season invests where OFCL doesn't exist yet), then fall back
      // to OFCL, then the first available model.
      const firstWithScenarios = metadata.models?.find(
        (m) => m.has_scenarios,
      )?.name;
      const defaultModel =
        firstWithScenarios ??
        (metadata.weather_models.includes("OFCL")
          ? "OFCL"
          : metadata.weather_models[0]);
      setSelectedModel(defaultModel);

      // Reset deviations to defaults
      setDeviations({
        intensity: 100, // Default intensity 100%
        track: 0, // Default track deviation 0
        speed: 100, // Default speed deviation 100%
      });
    }
  }, [metadata]);

  // Build scenario params for fetching
  const scenarioParams = useMemo<ScenarioQueryParams | null>(() => {
    if (!selectedStorm || !selectedModel) return null;

    return {
      weather_model_name: selectedModel,
      intensity: deviations.intensity,
      track: deviations.track,
      speed: deviations.speed,
    };
  }, [selectedStorm, selectedModel, deviations]);

  // Fetch scenario snapshot
  const {
    data: currentSnapshot,
    loading: scenarioLoading,
    isFetching: scenarioFetching,
    error: scenarioError,
  } = useStormScenario(
    selectedStorm?.id || null,
    scenarioParams,
  );

  const scenarioErrorMessage =
    scenarioError && typeof scenarioError === "object"
      ? (scenarioError as ApiError).message
      : null;
  const isScenarioNotFound =
    typeof scenarioErrorMessage === "string" &&
    scenarioErrorMessage.toLowerCase().includes("scenario not found");

  useEffect(() => {
    if (isScenarioNotFound) {
      setHoldEmptyScenarioData(true);
    }
  }, [isScenarioNotFound]);

  const isScenarioSnapshotPending = scenarioLoading || scenarioFetching;
  const shouldForceEmptyScenarioData =
    isScenarioNotFound || (holdEmptyScenarioData && isScenarioSnapshotPending);

  useEffect(() => {
    if (!holdEmptyScenarioData) return;
    if (isScenarioSnapshotPending) return;
    if (isScenarioNotFound) return;
    if (!currentSnapshot?.id) return;
    setHoldEmptyScenarioData(false);
  }, [
    holdEmptyScenarioData,
    isScenarioSnapshotPending,
    isScenarioNotFound,
    currentSnapshot?.id,
  ]);

  const activeSnapshotId = shouldForceEmptyScenarioData
    ? null
    : (currentSnapshot?.id ?? null);

  // Fetch track GeoJSON for current snapshot
  const {
    data: trackData,
    loading: trackLoading,
    isFetching: trackFetching,
  } = useTrackGeoJSON(
    activeSnapshotId,
  );

  // Fetch impacts for current snapshot
  const {
    data: impacts = [],
    loading: impactsLoading,
    isFetching: impactsFetching,
  } = useStormImpacts(
    activeSnapshotId,
  );

  const displaySnapshot = shouldForceEmptyScenarioData ? null : currentSnapshot;
  const displayTrackData = shouldForceEmptyScenarioData ? null : trackData;
  const displayImpacts = shouldForceEmptyScenarioData ? [] : impacts;

  // Clear rendering state after data is loaded and map has time to render
  const isScenarioRequestActive =
    scenarioLoading ||
    scenarioFetching ||
    trackLoading ||
    trackFetching ||
    impactsLoading ||
    impactsFetching;

  useEffect(() => {
    if (isRendering && !isScenarioRequestActive) {
      // Data has finished loading, wait 200ms for map to render
      const timer = setTimeout(() => {
        setIsRendering(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isRendering, isScenarioRequestActive]);

  useEffect(() => {
    if (hasLoadedOnce) return;
    if (!selectedStorm) return;
    if (stormsLoading || impactModelsLoading || metadataLoading) return;
    if (isScenarioRequestActive) return;
    setHasLoadedOnce(true);
  }, [
    hasLoadedOnce,
    selectedStorm,
    stormsLoading,
    impactModelsLoading,
    metadataLoading,
    isScenarioRequestActive,
  ]);

  useEffect(() => {
    if (!isStormTransition) return;
    if (metadataLoading || metadataFetching || isScenarioRequestActive) return;
    setIsStormTransition(false);
  }, [
    isStormTransition,
    metadataLoading,
    metadataFetching,
    isScenarioRequestActive,
  ]);

  // ============================================================================
  // Computed values
  // ============================================================================

  // Legend configuration based on selected property and impact model bins
  const legendConfig = useMemo(() => {
    // Default fallback
    const fallback = {
      label: "Impact Risk",
      units: "Customers",
      bins: [],
    };

    if (!selectedImpactModel) return fallback;

    const variable = selectedImpactModel.variables.find(
      (v: StormImpactVariable) => v.variable_slug === selectedProperty,
    );

    if (!variable) return fallback;

    return {
      label: variable.variable_name,
      units: variable.variable_units ?? "",
      bins: variable.bins.map((b: StormImpactVariableBin) => {
        const min = b.bin_min;
        const max = b.bin_max ?? Infinity;
        const isLastBin = b.bin_max == null || b.bin_max >= 999999990;
        const range = isLastBin
          ? `${min.toLocaleString()}+`
          : `${min.toLocaleString()} – ${b.bin_max!.toLocaleString()}`;
        const label = range;
        return { min, max, color: b.bin_color ?? "#d1d5db", label };
      }),
    };
  }, [selectedImpactModel, selectedProperty]);

  // Extract track coordinates for polyline
  const trackCoordinates = useMemo(() => {
    if (!displayTrackData || !displayTrackData.features) return [];

    const lineFeature = displayTrackData.features.find(
      (f) => f.geometry.type === "LineString",
    );
    if (!lineFeature) return [];

    const coords = lineFeature.geometry.coordinates as number[][];
    // Convert [lon, lat] to [lat, lon] for Leaflet
    return coords.map((coord) => [coord[1], coord[0]] as [number, number]);
  }, [displayTrackData]);

  // Current position (last point on track - where storm is heading)
  const currentPosition = useMemo(() => {
    if (trackCoordinates.length === 0) return null;
    return trackCoordinates[trackCoordinates.length - 1];
  }, [trackCoordinates]);

  // Starting position (first point on track - where storm originated)
  const startPosition = useMemo(() => {
    if (trackCoordinates.length === 0) return null;
    return trackCoordinates[0];
  }, [trackCoordinates]);

  // Total estimated outages
  const totalOutages = useMemo(() => {
    return displayImpacts.reduce(
      (sum, i) => sum + (i.variable_values.customersAffected ?? i.variable_values.customersInterrupted ?? 0),
      0,
    );
  }, [displayImpacts]);

  // Max wind speed from track nodes (we'll get this from the snapshot data if available)
  const maxWindSpeed = useMemo(() => {
    // Try to get from impacts data first
    const speeds = displayImpacts
      .map((i) => i.variable_values.peakWindSpeed)
      .filter((s): s is number => s !== undefined);
    return speeds.length > 0 ? Math.max(...speeds) : 0;
  }, [displayImpacts]);

  const category = getCategoryFromWind(maxWindSpeed);

  // Map center based on track
  const mapCenter = useMemo((): [number, number] => {
    if (trackCoordinates.length > 0) {
      const midIdx = Math.floor(trackCoordinates.length / 2);
      return trackCoordinates[midIdx];
    }
    return [28, -75]; // Default to Atlantic
  }, [trackCoordinates]);

  // ============================================================================
  // GeoJSON for Geographic Areas
  // ============================================================================

  // Create a map of geographic_node_id -> impact data for quick lookups
  const impactsByNodeId = useMemo(() => {
    const map: Record<string, ImpactForecast> = {};
    for (const impact of displayImpacts) {
      map[impact.geographic_node_id] = impact;
    }
    return map;
  }, [displayImpacts]);

  const hipPropertySelectorRef = useRef<HTMLDivElement>(null);

  // Keep ref for latest data (used in styling callbacks)
  const impactsByNodeIdRef = useRef(impactsByNodeId);
  impactsByNodeIdRef.current = impactsByNodeId;

  // Transform impacts to GeoJSON FeatureCollection
  const mapGeoJSON = useMemo(() => {
    if (!displayImpacts || displayImpacts.length === 0) {
      return null;
    }

    const features = displayImpacts
      .filter((impact) => impact.geometry)
      .map((impact) => ({
        type: "Feature",
        geometry: impact.geometry,
        properties: {
          id: impact.geographic_node_id,
          name: impact.geographic_node_name,
          resolution: "District",
          ...impact.variable_values,
        },
      }));

    if (features.length === 0) return null;

    return {
      type: "FeatureCollection",
      features: features,
    };
  }, [displayImpacts]);

  // Remount key for the GeoJSON layer.
  //
  // react-leaflet only applies `style` (and binds feature event handlers) when the
  // layer mounts, so GeoJSONLayer forces a fresh mount whenever this key changes.
  // Three things must trigger a remount:
  //
  //   1. Storm switch — selectedStorm.id is prefixed so the key changes immediately,
  //      keeping geoDataKey in sync with fitBoundsKey before the fetch resolves.
  //
  //   2. New impact data for the same storm (scenario slider change) — displayImpacts
  //      is a dep so React recomputes; the hash captures the active property's values so
  //      a new snapshot with different peakWindSpeed (or any selectedProperty) produces a
  //      different key even when customer counts are unchanged.
  //
  //   3. Property switch — selectedProperty is both a dep and embedded in the key suffix,
  //      so switching the displayed variable always forces a remount.
  //
  // The hash mirrors getFeatureStyle's value lookup (variable_values[selectedProperty]
  // with a customersInterrupted fallback for customersAffected) so the hashed values
  // exactly match what determines choropleth color.
  const geoDataKey = useMemo(() => {
    const stormId = selectedStorm?.id ?? "none";
    if (!displayImpacts || displayImpacts.length === 0)
      return `storm-${stormId}-no-impacts`;
    let hash = 0;
    for (const impact of displayImpacts) {
      const rawValue = impact.variable_values[selectedProperty];
      const value =
        rawValue !== undefined
          ? rawValue
          : selectedProperty === "customersAffected"
            ? impact.variable_values.customersInterrupted ?? 0
            : 0;
      const token = `${impact.geographic_node_id}:${value};`;
      for (let k = 0; k < token.length; k++) {
        hash = (hash * 31 + token.charCodeAt(k)) | 0;
      }
    }
    return `storm-${stormId}-n${displayImpacts.length}-h${hash >>> 0}-${selectedProperty}`;
  }, [displayImpacts, selectedProperty, selectedStorm?.id]);

  // Get color based on selected property
  const getColorForValue = useCallback(
    (value: number): string => {
      if (!legendConfig || !legendConfig.bins) return "#d1d5db";

      const bin = legendConfig.bins.find(
        (b: { min: number; max: number; color: string; label: string }) =>
          value >= b.min && value < b.max,
      );
      return bin ? bin.color : "#d1d5db";
    },
    [legendConfig],
  );

  // Style function for GeoJSON features
  const getFeatureStyle = useCallback(
    (feature: any): PathOptions => {
      const nodeId = feature.properties?.id;
      const impact = impactsByNodeIdRef.current[nodeId];

      let value = 0;
      if (impact && impact.variable_values) {
        const rawValue = impact.variable_values[selectedProperty];
        if (rawValue !== undefined) {
          value = rawValue;
        } else if (selectedProperty === "customersAffected") {
          // Fallback for customersAffected -> customersInterrupted
          value = impact.variable_values.customersInterrupted ?? 0;
        }
      }

      const fillColor = getColorForValue(value);

      return {
        color: "#9ca3af",
        weight: 1.5,
        opacity: 1,
        fillColor,
        fillOpacity: 0.6,
      };
    },
    [getColorForValue, selectedProperty],
  );

  // Popup content for features - using SIP-style styling
  const generatePopupContent = useCallback(
    (props: any): string => {
      const nodeId = props?.id;
      const impact = impactsByNodeIdRef.current[nodeId];
      const name = props?.name || "Unknown";
      const resolution = props?.resolution || null;
      const hasData = !!impact;

      // Get the selected property value dynamically
      let selectedValue = impact?.variable_values?.[selectedProperty] ?? 0;
      if (selectedValue === 0 && selectedProperty === "customersAffected") {
        selectedValue = impact?.variable_values?.customersInterrupted ?? 0;
      }

      let content = '<div class="weather-threat-tooltip">';

      // Header section - matching SIP style
      content +=
        `<div class="threat-tooltip-header">` +
        `<h3 class="threat-tooltip-title">${name}</h3>` +
        (resolution
          ? `<div class="resolution-info">Resolution: ${resolution}</div>`
          : "") +
        `</div>`;

      // Main data section - Dynamic based on selected property
      content += '<div class="selected-variable-section">';
      if (hasData) {
        // Format the value appropriately based on property type
        let formattedValue: string;
        if (selectedProperty === "peakWindSpeed") {
          formattedValue = Math.round(selectedValue).toLocaleString() + " mph";
        } else {
          formattedValue = Math.round(selectedValue).toLocaleString();
        }

        content +=
          '<div class="selected-variable-display">' +
          `<span class="selected-variable-label">${legendConfig.label.toUpperCase()}</span>` +
          `<span class="selected-variable-data">${formattedValue}</span>` +
          `<span class="selected-variable-units">${legendConfig.units}</span>` +
          "</div>";
      } else {
        content +=
          '<div class="no-data-available">No Impact Data Available</div>';
      }
      content += "</div>";

      content += "</div>";
      return content;
    },
    [selectedProperty, legendConfig],
  );

  // Event handlers for each feature - using popup like SIP for richer interaction
  const onEachFeature = useCallback(
    (feature: any, layer: any) => {
      const props = feature.properties;

      // Bind popup with SIP-style options
      layer.bindPopup(() => generatePopupContent(props), {
        maxWidth: isCompactLayout ? 260 : 320,
        minWidth: isCompactLayout ? 200 : 280,
        autoPan: false,
        closeButton: false,
        className: "weather-threat-popup",
      });

      // Hover and interaction effects - matching SIP behavior
      layer.on({
        mouseover: (e: any) => {
          const target = e.target;
          target.setStyle({
            weight: 3,
            color: "#6b7280",
            fillOpacity: 0.85,
          });
          target.bringToFront();
          layer.openPopup(e.latlng);
        },
        mouseout: (e: any) => {
          const target = e.target;
          target.setStyle(getFeatureStyle(feature));
          layer.closePopup();
        },
        mousemove: (e: any) => {
          layer.getPopup()?.setLatLng(e.latlng);
        },
      });
    },
    [generatePopupContent, getFeatureStyle, isCompactLayout],
  );

  // Handlers
  const handlePropertyChange = useCallback((propertyId: string) => {
    setSelectedProperty(propertyId);
  }, []);

  const handleStormSelect = useCallback((storm: NamedStorm) => {
    trackRumAction('hip_storm_selected', {
      storm_id: storm.id,
      storm_name: storm.name,
      is_active: storm.active,
    });
    if (storm.id !== selectedStorm?.id) {
      setIsStormTransition(true);
      setIsRendering(false);
    }
    setSelectedStorm(storm);
  }, [selectedStorm?.id]);

  const handleModelChange = useCallback((model: string) => {
    setSelectedModel(model);
    // Reset deviations to canonical defaults whenever the model changes so
    // scenarioParams changes immediately and React Query fires a new fetch.
    setDeviations({ intensity: 100, track: 0, speed: 100 });
    // Set rendering state to keep loading notification visible until data is plotted
    setIsRendering(true);
  }, []);

  const handleDeviationChange = useCallback(
    (newDeviations: ScenarioDeviations) => {
      setDeviations(newDeviations);
    },
    [],
  );

  // Unified loading states - grouped to prevent flickering
  const isBootstrapLoading =
    stormsLoading ||
    impactModelsLoading ||
    metadataLoading ||
    scenarioLoading ||
    trackLoading ||
    impactsLoading;
  const stormTransitionLoading =
    isStormTransition &&
    (metadataLoading || metadataFetching || isScenarioRequestActive);
  const showMapOverlay = !hasLoadedOnce ? isBootstrapLoading : stormTransitionLoading;
  const showForecastPill =
    hasLoadedOnce &&
    !stormTransitionLoading &&
    !isScenarioNotFound &&
    (scenarioFetching || trackFetching || impactsFetching || isRendering);
  const impactsTableLoading =
    isStormTransition ||
    isScenarioRequestActive ||
    isRendering;

  // Format the run time for display
  const runTimeDisplay = useMemo(() => {
    if (!displaySnapshot?.run_time) return undefined;
    try {
      return `Run: ${new Date(displaySnapshot.run_time).toLocaleString()}`;
    } catch {
      return undefined;
    }
  }, [displaySnapshot]);

  // Build property options for selector from impact model variables
  // Keep options in sync with the active scenario response so stale variables
  // from a previous model are not shown.
  const availableImpactVariableSlugs = useMemo(() => {
    const keys = new Set<string>();

    displayImpacts.forEach((impact) => {
      Object.entries(impact.variable_values || {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          keys.add(key);
        }
      });
    });

    // Maintain compatibility for customersAffected fallback logic.
    if (
      !keys.has("customersAffected") &&
      displayImpacts.some(
        (impact) => impact.variable_values.customersInterrupted !== undefined,
      )
    ) {
      keys.add("customersAffected");
    }

    return keys;
  }, [displayImpacts]);

  // Build property options for selector from selected impact model variables,
  // filtered by what exists in the active scenario response.
  const propertyOptions = useMemo(() => {
    if (!selectedImpactModel) return [];

    return selectedImpactModel.variables
      .filter((v: StormImpactVariable) => v.bins && v.bins.length > 0)
      .filter((v: StormImpactVariable) =>
        availableImpactVariableSlugs.has(v.variable_slug),
      )
      .sort(
        (a: StormImpactVariable, b: StormImpactVariable) =>
          (a.variable_order || 0) - (b.variable_order || 0),
      )
      .map((v: StormImpactVariable) => ({
        id: v.variable_slug,
        label: v.variable_name,
        units: v.variable_units,
        description: v.variable_description,
        type: v.variable_type_name,
      }));
  }, [selectedImpactModel, availableImpactVariableSlugs]);

  // Ensure selected property is valid for the active model/response.
  useEffect(() => {
    if (propertyOptions.length === 0) return;

    const isValid = propertyOptions.some((p) => p.id === selectedProperty);
    if (isValid) return;

    const preferredDefaults = [
      "customersAffected",
      "incidents",
      "peakWindSpeed",
    ];
    const nextProperty =
      preferredDefaults.find((id) =>
        propertyOptions.some((p) => p.id === id),
      ) || propertyOptions[0].id;

    setSelectedProperty(nextProperty);
  }, [propertyOptions, selectedProperty]);

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Global Header */}
      <Header />

      {/* Sub-header with HIP controls */}
      <div className="flex flex-shrink-0 flex-col gap-2 border-b bg-background px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="min-w-0 truncate text-base font-semibold sm:text-lg">
            Hurricane Impact Predictions
          </h1>
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            HIP v1.0
          </Badge>
        </div>

        {/* Controls */}
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:mr-2 sm:gap-3">
          {/* Storm Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="min-w-0 max-w-full gap-2"
                disabled={stormsLoading}
              >
                <Navigation className="h-4 w-4" />
                <span className="min-w-0 truncate">
                  {selectedStorm?.name || "Select Storm"}
                </span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {storms.length === 0 ? (
                <DropdownMenuItem disabled>
                  No storms available
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuLabel className="text-muted-foreground">
                    Active Storms
                  </DropdownMenuLabel>
                  {storms.filter((s) => s.active).length === 0 ? (
                    <DropdownMenuItem disabled>
                      No active storms
                    </DropdownMenuItem>
                  ) : (
                    storms
                      .filter((s) => s.active)
                      .map((storm) => (
                        <DropdownMenuItem
                          key={storm.id}
                          onClick={() => handleStormSelect(storm)}
                          className="flex items-center gap-2"
                        >
                          <Check
                            className={`h-4 w-4 ${
                              selectedStorm?.id === storm.id
                                ? "opacity-100"
                                : "opacity-0"
                            }`}
                          />
                          {storm.name} ({storm.year})
                        </DropdownMenuItem>
                      ))
                  )}

                  <DropdownMenuSeparator />

                  <DropdownMenuLabel className="text-muted-foreground">
                    Inactive Storms
                  </DropdownMenuLabel>
                  {storms.filter((s) => !s.active).length === 0 ? (
                    <DropdownMenuItem disabled>
                      No inactive storms
                    </DropdownMenuItem>
                  ) : (
                    storms
                      .filter((s) => !s.active)
                      .map((storm) => (
                        <DropdownMenuItem
                          key={storm.id}
                          onClick={() => handleStormSelect(storm)}
                          className="flex items-center gap-2"
                        >
                          <Check
                            className={`h-4 w-4 ${
                              selectedStorm?.id === storm.id
                                ? "opacity-100"
                                : "opacity-0"
                            }`}
                          />
                          {storm.name} ({storm.year})
                        </DropdownMenuItem>
                      ))
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative flex min-w-0 flex-1 flex-col overflow-y-auto lg:overflow-hidden">
        {/* Map */}
        <div className="relative z-10 h-[45dvh] min-h-[320px] shrink-0 lg:min-h-0 lg:flex-1">
          <MapLoaderOverlay
            loading={showMapOverlay}
            text={!hasLoadedOnce ? "Loading..." : "Loading map data..."}
          />

          {/* Subtle forecast loading indicator for scenario updates after page is loaded */}
          {showForecastPill && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 bg-background/90 backdrop-blur-sm border border-border rounded-full px-4 py-1.5 shadow-sm flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-xs text-muted-foreground font-medium">
                Loading scenario...
              </span>
            </div>
          )}
          {isScenarioNotFound && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 bg-destructive/10 text-destructive border border-destructive/30 rounded-full px-4 py-1.5 shadow-sm">
              <span className="text-xs font-medium">
                Scenario not found for selected model
              </span>
            </div>
          )}

          <BaseMap center={mapCenter} zoom={5} className="h-full w-full">
            {/* Fit map to hurricane path when track is available; otherwise GeoJSON will fit to impact zones */}
            {trackCoordinates.length > 0 && (
              <FitBoundsToTrack
                trackCoordinates={trackCoordinates}
                dataKey={`storm-${selectedStorm?.id}`}
                padding={80}
              />
            )}
            {/* Geographic areas with impact coloring */}
            {mapGeoJSON && (
              <GeoJSONLayer
                dataKey={`${geoDataKey}-${isCompactLayout ? "compact" : "full"}`}
                fitBoundsKey={`storm-${selectedStorm?.id}`}
                data={mapGeoJSON as any}
                style={getFeatureStyle}
                onEachFeature={onEachFeature}
                fitBounds={trackCoordinates.length === 0}
              />
            )}

            {/* Track line - always visible when data is available */}
            {trackCoordinates.length > 0 && (
              <Polyline
                positions={trackCoordinates}
                pathOptions={{
                  color: "#dc2626",
                  weight: 4,
                  opacity: 0.9,
                }}
              />
            )}

            {/* Storm origin marker (start of track) */}
            {startPosition && trackCoordinates.length > 1 && (
              <Marker position={startPosition} icon={stormOriginIcon}>
                <Popup>
                  <div className="p-2">
                    <div className="font-bold">Storm Origin</div>
                    <div className="text-sm text-muted-foreground">
                      Track start point
                    </div>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Current position marker (hurricane icon) */}
            {currentPosition && (
              <Marker position={currentPosition} icon={hurricaneIcon}>
                <Popup>
                  <div className="p-2">
                    <div className="font-bold">{selectedStorm?.name}</div>
                    {category > 0 && (
                      <div className="text-sm">Category {category}</div>
                    )}
                    {maxWindSpeed > 0 && (
                      <div className="text-sm">
                        {Math.round(maxWindSpeed)} mph
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      Current/Projected Position
                    </div>
                  </div>
                </Popup>
              </Marker>
            )}
          </BaseMap>

          {/* Scenario Controls - no wrapper div, let AbstractInset handle positioning */}
          {!isCompactLayout && (
            <ScenarioControls
              metadata={metadata || null}
              values={deviations}
              selectedModel={selectedModel}
              onModelChange={handleModelChange}
              onChange={handleDeviationChange}
              isLoading={stormTransitionLoading || isScenarioRequestActive}
              position="top-left"
              width="400px"
              title="Scenario Controls"
              subtitle={runTimeDisplay}
              autoUpdate={autoUpdate}
              initiallyCollapsed={false}
            />
          )}

          {/* Hurricane Info Card - Draggable */}
          {!isCompactLayout && (
            <HIPInfoCard
              stormName={selectedStorm?.name}
              stormExternalId={selectedStorm?.external_id}
              category={category}
              maxWindSpeed={maxWindSpeed}
              selectedModel={selectedModel}
              intensity={deviations.intensity}
              trackShift={deviations.track}
              districtsAffected={displayImpacts.length}
              totalOutages={totalOutages}
              position="middle-right"
              width="300px"
              initiallyCollapsed={false}
            />
          )}

          {/* Impacts Table - no wrapper div, let AbstractInset handle positioning */}
          {!isCompactLayout && (
            <HIPImpactsTable
              impacts={displayImpacts}
              loading={impactsTableLoading}
              error={isScenarioNotFound ? "Scenario not found" : null}
              position="bottom-left"
              width="450px"
              height="350px"
              title="Impact Predictions"
              subtitle={selectedModel ? `${selectedModel} Model` : undefined}
              initiallyCollapsed={false}
              selectorVariables={propertyOptions.map((p) => ({
                id: p.id,
                label: p.label,
                units: p.units,
              }))}
              modelVariables={selectedImpactModel?.variables.map((v) => ({
                slug: v.variable_slug,
                name: v.variable_name,
                units: v.variable_units,
                order: v.variable_order,
              }))}
            />
          )}

          {/* Property Selector */}
          <MapPropertySelector
            ref={hipPropertySelectorRef}
            properties={propertyOptions}
            selectedProperty={
              propertyOptions.find(
                (p: {
                  id: string;
                  label: string;
                  units: string;
                  description: string;
                  type: string;
                }) => p.id === selectedProperty,
              ) || undefined
            }
            onPropertyChange={handlePropertyChange}
            position="top-right"
          />

          {/* Legend - Using reusable MapLegend component like SIP */}
          <MapLegend
            propertyName={legendConfig.label}
            units={legendConfig.units}
            bins={legendConfig.bins}
            position="bottom-right"
            collapsed={legendCollapsed}
            onToggle={() => setLegendCollapsed(!legendCollapsed)}
            overlapRef={hipPropertySelectorRef}
          />
        </div>

        {isCompactLayout && (
        <div className="grid min-w-0 gap-3 bg-muted/40 p-3 lg:hidden">
          <ScenarioControls
            metadata={metadata || null}
            values={deviations}
            selectedModel={selectedModel}
            onModelChange={handleModelChange}
            onChange={handleDeviationChange}
            isLoading={stormTransitionLoading || isScenarioRequestActive}
            title="Scenario Controls"
            subtitle={runTimeDisplay}
            autoUpdate={autoUpdate}
            initiallyCollapsed={false}
            isCollapsible={false}
            panelMode
          />
          <HIPInfoCard
            stormName={selectedStorm?.name}
            stormExternalId={selectedStorm?.external_id}
            category={category}
            maxWindSpeed={maxWindSpeed}
            selectedModel={selectedModel}
            intensity={deviations.intensity}
            trackShift={deviations.track}
            districtsAffected={displayImpacts.length}
            totalOutages={totalOutages}
            initiallyCollapsed={false}
            isCollapsible={false}
            panelMode
          />
          <HIPImpactsTable
            impacts={displayImpacts}
            loading={impactsTableLoading}
            error={isScenarioNotFound ? "Scenario not found" : null}
            height="360px"
            title="Impact Predictions"
            subtitle={selectedModel ? `${selectedModel} Model` : undefined}
            initiallyCollapsed={false}
            isCollapsible={false}
            panelMode
            selectorVariables={propertyOptions.map((p) => ({
              id: p.id,
              label: p.label,
              units: p.units,
            }))}
            modelVariables={selectedImpactModel?.variables.map((v) => ({
              slug: v.variable_slug,
              name: v.variable_name,
              units: v.variable_units,
              order: v.variable_order,
            }))}
          />
        </div>
        )}
      </div>
    </div>
  );
}
